/**
 * Chrome Extension API utilities
 * Provides helpers for detecting Chrome extension context and accessing Chrome APIs
 */

/**
 * Check if Chrome extension APIs are available
 * @returns {boolean} True if chrome.tabs API is available
 */
export function isChromeExtensionAvailable() {
  return (
    typeof chrome !== 'undefined' &&
    chrome.tabs &&
    typeof chrome.tabs.query === 'function'
  );
}

/**
 * Parse Onshape URL to extract document context
 * Supports formats:
 * - /documents/{did}/w/{wid}/e/{eid} (workspace)
 * - /documents/{did}/v/{vid}/e/{eid} (version)
 * @param {string} url - Onshape document URL
 * @returns {Object|null} { documentId, workspaceId, elementId, server } or null if not parseable
 */
export function parseOnshapeUrl(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  // Check if it's an Onshape URL
  const onshapeDomainMatch = url.match(/https?:\/\/([^\/]+\.onshape\.com)/);
  if (!onshapeDomainMatch) {
    return null;
  }

  const server = `https://${onshapeDomainMatch[1]}`;

  // Parse document ID: /documents/{did}
  const docMatch = url.match(/\/documents\/([a-f0-9]{24})/);
  if (!docMatch) {
    return null;
  }

  const documentId = docMatch[1];

  // Parse workspace or version: /w/{wid} or /v/{vid}
  const workspaceMatch = url.match(/\/w\/([a-f0-9]{24})/);
  const versionMatch = url.match(/\/v\/([a-f0-9]{24})/);
  
  const workspaceId = workspaceMatch ? workspaceMatch[1] : (versionMatch ? versionMatch[1] : null);
  
  // Parse element ID: /e/{eid}
  const elementMatch = url.match(/\/e\/([a-f0-9]{24})/);
  const elementId = elementMatch ? elementMatch[1] : null;

  // Return context if we have at least documentId
  if (documentId) {
    return {
      documentId,
      workspaceId,
      elementId,
      server,
    };
  }

  return null;
}

/**
 * Get the active tab's URL using Chrome Tabs API
 * For sidePanel contexts, we need to find the Onshape tab (may not be "active" in currentWindow)
 * @returns {Promise<string|null>} Active tab URL or null if unavailable
 */
export async function getActiveTabUrl() {
  if (!isChromeExtensionAvailable()) {
    console.log('[chrome-extension] Chrome tabs API not available');
    return null;
  }

  try {
    // First try: Get active tab in current window
    let tabs = await chrome.tabs.query({ active: true, currentWindow: true });
    console.log('[chrome-extension] Active tab query result:', tabs.length, 'tabs');
    
    if (tabs && tabs.length > 0 && tabs[0].url) {
      const url = tabs[0].url;
      console.log('[chrome-extension] Active tab URL:', url);
      
      // If it's an Onshape URL, return it
      if (url.includes('onshape.com')) {
        return url;
      }
      
      // If not Onshape, try to find an Onshape tab in all windows
      console.log('[chrome-extension] Active tab is not Onshape, searching for Onshape tabs...');
    }
    
    // Second try: Find any Onshape tab (sidePanel might be separate from the Onshape tab)
    tabs = await chrome.tabs.query({ url: '*://*.onshape.com/*' });
    console.log('[chrome-extension] Found', tabs.length, 'Onshape tabs');
    
    if (tabs && tabs.length > 0) {
      // Prefer tabs that are in a window (not just background)
      const visibleTabs = tabs.filter(tab => tab.windowId !== undefined);
      const tabToUse = visibleTabs.length > 0 ? visibleTabs[0] : tabs[0];
      
      if (tabToUse.url) {
        console.log('[chrome-extension] Using Onshape tab URL:', tabToUse.url);
        return tabToUse.url;
      }
    }
    
    console.log('[chrome-extension] No Onshape tab found');
    return null;
  } catch (error) {
    // Handle specific error cases
    console.error('[chrome-extension] Error querying tabs:', error);
    if (error.message && error.message.includes('Cannot access')) {
      console.warn('[chrome-extension] Cannot access tabs API. Extension may need "tabs" permission in manifest.json');
    } else if (error.message && error.message.includes('No matching')) {
      console.log('[chrome-extension] No matching tabs found (this is OK if no Onshape tabs are open)');
    } else {
      console.warn('[chrome-extension] Error querying tabs:', error.message || error);
    }
    return null;
  }
}

/**
 * Get Onshape context from active tab URL
 * @returns {Promise<Object|null>} Parsed Onshape context or null
 */
export async function getOnshapeContextFromActiveTab() {
  const url = await getActiveTabUrl();
  if (!url) {
    return null;
  }

  // Check if it's an Onshape URL before parsing
  if (!url.includes('onshape.com')) {
    console.log('[chrome-extension] Active tab is not an Onshape URL:', url);
    return null;
  }

  const context = parseOnshapeUrl(url);
  if (!context) {
    console.log('[chrome-extension] Could not parse Onshape URL. Make sure you are viewing a document (not just the Onshape homepage).');
  }
  
  return context;
}

